import { getTokenIconUrl } from 'utils/url';
import { Body2 } from 'components/Typography';
import React, { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useClickOutside } from 'utils/hooks/useClickOutside';
import { TokenImage } from 'components/TokenImage';
import { useBalance, useConnection, useReadContracts } from 'wagmi';
import { ERC20ABI } from 'structure/pv2/vault/components/abi/ERC20ABI';
import { ethers } from 'ethers';
import { isNativeTokenAddress } from 'structure/pv2/vault/utils/nativeDeposit';

interface Token {
  symbol: string;
  name: string;
  decimals: number;
  address: `0x${string}`;
}

interface SelectTokenProps {
  open: boolean;
  value: Token;
  options: Token[];
  chainId: number;
  onChange: (value: Token) => void;
  onClickOutside: () => void;
}

export const SelectToken = (props: SelectTokenProps) => {
  const { open, options, chainId, onClickOutside, onChange } = props;

  const account = useConnection();

  const handleItemClick = (token: Token) => {
    onChange(token);
  };

  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, onClickOutside, open);

  const erc20Options = options.filter(
    (token) => !isNativeTokenAddress(token.address)
  );

  const nativeBalanceQuery = useBalance({
    address: account.address,
    chainId,
    query: {
      enabled:
        account.isConnected &&
        options.some((token) => isNativeTokenAddress(token.address)),
    },
  });

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const balanceQuery = useReadContracts({
    contracts: erc20Options.map((el) => ({
      address: el.address,
      abi: ERC20ABI,
      chainId: chainId,
      functionName: 'balanceOf',
      args: [account.address],
      from: account.address,
    })),
    query: {
      enabled: account.isConnected && erc20Options.length > 0,
    },
  });

  const balances = options.reduce<Record<`0x${string}`, bigint>>(
    (acc, item) => {
      acc[item.address] = BigInt(0);

      if (isNativeTokenAddress(item.address)) {
        acc[item.address] = nativeBalanceQuery.data?.value ?? BigInt(0);
        return acc;
      }

      const erc20Index = erc20Options.findIndex(
        (token) => token.address === item.address
      );
      if (
        balanceQuery.data &&
        balanceQuery.data[erc20Index] &&
        balanceQuery.data[erc20Index].status === 'success'
      ) {
        acc[item.address] = balanceQuery.data[erc20Index].result as bigint;
      }
      return acc;
    },
    {} as Record<`0x${string}`, bigint>
  );

  const sortedOptions = [...options].sort((a, b) => {
    const balanceA = balances[a.address] || BigInt(0);
    const balanceB = balances[b.address] || BigInt(0);
    return balanceB > balanceA ? 1 : -1;
  });

  return (
    <div className='relative z-10 mt-[4px] w-full'>
      <div
        className={twMerge(
          'absolute h-[200px] w-full overflow-y-scroll border-[0.4px] border-[#333333] bg-[#1E1E1E]',
          open ? 'block' : 'hidden'
        )}
      >
        <div
          ref={ref}
          className='flex flex-col rounded-[8px] bg-[#1E1E1E] p-[10px] text-white'
        >
          {sortedOptions.map((token) => (
            <div
              key={token.address}
              onClick={() => handleItemClick(token)}
              className='flex cursor-pointer flex-row items-center gap-[10px] px-[6px] py-[10px]'
            >
              <TokenImage
                symbol={token.symbol}
                height={24}
                width={24}
                src={getTokenIconUrl(token.symbol)}
                alt={token.symbol}
              />
              <Body2>{token.name}</Body2>
              <Body2 className='ml-auto'>
                {ethers.utils.formatUnits(
                  balances[token.address],
                  token.decimals
                )}
              </Body2>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
